import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  useDeleteManualNodeMutation,
  setHighlightedNodeIds,
  useActiveBranchRecord,
  useAppDispatch,
  useAppSelector,
  useGetGraphQuery,
  useGetNodeDetailsQuery,
  useLazySearchGraphQuery,
  type GraphResponse
} from '@axilon-apps/domains-ontology-ui-state'
import { useSearchParams } from 'react-router-dom'

import { useProtectedBranchGuard } from '../../components/Branches/ProtectedBranchPromptDialog'
import { useChatAppContext } from '../../chat-app-context'
import { buildGraphAppContextEntries } from './graph-app-context'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Separator } from '../../components/ui/separator'
import { useDebugMode } from '../../lib/debug-mode'
import { cn } from '../../lib/utils'
import CosmosGraphCanvas, { type CosmosGraphCanvasHandle } from './CosmosGraphCanvas'
import GraphLegend from './GraphLegend'
import NodeEditModal from './NodeEditModal'

const GraphPage = () => {
  const dispatch = useAppDispatch()
  const debugMode = useDebugMode()
  const [searchParams] = useSearchParams()
  const highlightProcessedRef = React.useRef<string | null>(null)
  const handleClearHighlights = React.useCallback(() => {
    highlightProcessedRef.current = null
    dispatch(setHighlightedNodeIds([]))
  }, [dispatch])
  const [selectedNodeIds, setSelectedNodeIds] = React.useState<string[]>([])
  const selectedNodeId = selectedNodeIds[selectedNodeIds.length - 1] ?? null

  const handleToggleNode = React.useCallback((nodeId: string | null) => {
    if (!nodeId) {
      setSelectedNodeIds([])
      return
    }
    setSelectedNodeIds((prev) =>
      prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]
    )
  }, [])

  const [search, setSearch] = React.useState('')
  const graphCanvasRef = React.useRef<CosmosGraphCanvasHandle | null>(null)
  const highlightedNodeIds = useAppSelector((state) => state.graph.highlightedNodeIds)

  const {
    data: graphData,
    isLoading,
    isFetching,
    error,
    refetch: refetchGraph
  } = useGetGraphQuery({ includeAll: true })

  const [mergedGraph, setMergedGraph] = React.useState<GraphResponse | null>(null)
  const [searchGraph, searchState] = useLazySearchGraphQuery()
  const [deleteNode, deleteNodeState] = useDeleteManualNodeMutation()

  const [editModalOpen, setEditModalOpen] = React.useState(false)
  const activeBranchRecord = useActiveBranchRecord()
  const branchGuard = useProtectedBranchGuard(activeBranchRecord)

  React.useEffect(() => {
    if (!graphData) return
    setMergedGraph(graphData)
    setSelectedNodeIds([])
  }, [graphData])

  const highlightParam = searchParams.get('highlight')
  const highlightConsumedRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (highlightParam && highlightConsumedRef.current !== highlightParam) {
      highlightConsumedRef.current = highlightParam
      dispatch(setHighlightedNodeIds([highlightParam]))
    }
  }, [highlightParam, dispatch])

  React.useEffect(() => {
    if (highlightedNodeIds.length === 0 || !mergedGraph) return
    const targetId = highlightedNodeIds[0]
    if (highlightProcessedRef.current === targetId) return

    highlightProcessedRef.current = targetId
    if (mergedGraph.nodes.some((n) => n._id === targetId)) {
      setSelectedNodeIds([targetId])
      requestAnimationFrame(() => {
        graphCanvasRef.current?.revealNode(targetId)
        dispatch(setHighlightedNodeIds([]))
      })
    }
  }, [highlightedNodeIds, mergedGraph, dispatch])

  const selectedNode = mergedGraph?.nodes.find((node) => node._id === selectedNodeId) ?? null

  const {
    data: selectedNodeDetail,
    isFetching: detailFetching,
    error: detailError,
    refetch: refetchNodeDetail
  } = useGetNodeDetailsQuery(selectedNodeId as string, {
    skip: !selectedNodeId || !debugMode
  })

  const searchResults = searchState.data ?? []
  // The Node Details rail is a debug-only inspector, and an empty one would
  // still cost the 360 px grid track — so both flags gate layout, not just copy.
  const sideRailVisible = searchResults.length > 0 || (debugMode && selectedNode !== null)

  const handleSearch = async () => {
    if (search.trim()) {
      await searchGraph(search.trim())
    }
  }

  const handleDeleteNode = async () => {
    if (!selectedNodeId) return
    if (!branchGuard.ensureWritable()) return
    const nodeName = String(
      selectedNodeDetail?.name ?? selectedNode?.name ?? selectedNode?.id ?? selectedNodeId
    )
    if (
      !window.confirm(
        `Delete node "${nodeName}"? This permanently deletes the node and cannot be undone.`
      )
    ) {
      return
    }
    await deleteNode(selectedNodeId).unwrap()
    setSelectedNodeIds((prev) => prev.filter((id) => id !== selectedNodeId))
    void refetchGraph()
  }

  // Joy `<Button color="danger" variant="soft">` becomes the destructive-tinted
  // equivalent. `hover:text-st-down` is explicit because the local Button's
  // `outline` variant ships `hover:text-accent-foreground` (dark).
  const dangerSoftClass =
    'border-st-down/30 bg-st-down-bg text-st-down hover:bg-st-down-bg/80 hover:text-st-down'

  const isAnyBgWork = isLoading || isFetching || (!!selectedNodeId && detailFetching)

  useChatAppContext(
    buildGraphAppContextEntries({
      selectedNodeIds,
      highlightedNodeIds,
      search,
      searchResultCount: searchState.data?.length,
      graph: mergedGraph
    }),
    ['highlight']
  )

  return (
    // Joy `<Stack spacing={2}>` (16 px gap) → `flex flex-col gap-4`.
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Graph</h1>
        <p className="mt-1 text-sm text-ink-3">Explore and inspect the ontology graph.</p>
      </header>

      <Card className="py-4">
        <CardContent className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
          <div className="flex min-w-45 flex-1 flex-col gap-1.5">
            <Label htmlFor="graph-search">Search Nodes</Label>
            <Input
              id="graph-search"
              placeholder="Search by node name or id"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void handleSearch()
                }
              }}
            />
          </div>

          <div className="flex flex-row flex-wrap items-end gap-2">
            <Button
              variant="outline"
              className="border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand"
              disabled={isFetching}
              onClick={() => void refetchGraph()}
            >
              {isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Joy `<LinearProgress />` while any background work is in flight →
          slim indeterminate bar matching the global rhythm. */}
      {isAnyBgWork ? (
        <div className="h-1 w-full overflow-hidden rounded-full bg-bg-elev">
          <div
            className="h-full w-1/3 bg-brand"
            style={{ animation: 'indeterminate 1.2s ease-in-out infinite' }}
          />
          <style>{`@keyframes indeterminate { 0% { margin-left: -33%; } 100% { margin-left: 100%; } }`}</style>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-md bg-st-down-bg p-2 text-sm text-st-down">
          Failed to load graph data.
        </div>
      ) : null}

      {/* Joy `<Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', xl:
          'minmax(0,1fr) 360px' }, gap:2 }}>` → CSS grid that collapses to a
          single column below xl (1280 px in Tailwind v4 default). The second
          track only exists while the rail has content, so the canvas reclaims
          its 360 px otherwise. The rail is min-w-0 + max-w 360 so long
          property strings cannot expand the column. */}
      <div
        data-testid="graph-page-grid"
        className={cn(
          'grid min-w-0 grid-cols-1 gap-4',
          // Fixed 360px track plus `min-w-0` on the rail so a long
          // unbreakable property cannot expand the column.
          sideRailVisible && 'xl:grid-cols-[minmax(0,1fr)_22.5rem]'
        )}
      >
        <Card className="min-h-120 py-4">
          <CardContent className="flex flex-col gap-3">
            {/* Title-on-top, buttons-row-below layout — see original Joy
                comment for the rationale (chat sidebar narrows the column). */}
            <div className="flex flex-col gap-1.5">
              <div className="min-w-0">
                <h3 className="m-0 text-base font-semibold">Graph Canvas</h3>
                <p data-tour-id="graph-canvas-counts" className="m-0 text-sm text-ink-3">
                  {mergedGraph
                    ? `${mergedGraph.nodes.length} nodes, ${mergedGraph.relationships.length} relationships.`
                    : 'Loading graph.'}{' '}
                  Hover to preview neighborhoods.
                </p>
              </div>
              <div className="flex flex-row flex-wrap items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand"
                  disabled={selectedNodeIds.length === 0}
                  onClick={() => setSelectedNodeIds([])}
                >
                  Deselect All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand"
                  onClick={() => graphCanvasRef.current?.fit()}
                >
                  Fit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand"
                  onClick={() => graphCanvasRef.current?.zoomIn()}
                >
                  Zoom In
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand"
                  onClick={() => graphCanvasRef.current?.zoomOut()}
                >
                  Zoom Out
                </Button>
              </div>
            </div>

            {mergedGraph ? (
              <div className="mt-1 flex flex-col gap-2">
                <CosmosGraphCanvas
                  ref={graphCanvasRef}
                  graph={mergedGraph}
                  selectedNodeIds={selectedNodeIds}
                  highlightedNodeIds={highlightedNodeIds}
                  onToggleNode={handleToggleNode}
                  onClearHighlights={handleClearHighlights}
                  height="calc(100vh - 30rem)"
                />
                <div data-tour-id="graph-node-types">
                  <GraphLegend graph={mergedGraph} />
                </div>
              </div>
            ) : (
              // Joy `<Sheet variant="soft">` → softly-tinted card-sunk surface.
              <div className="rounded-md bg-bg-sunk p-6">
                <p className="m-0 text-sm text-ink-3">
                  Graph data will appear here once the API responds.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {sideRailVisible ? (
          <div
            data-testid="graph-side-rail"
            className="flex min-w-0 w-full max-w-full flex-col gap-4 overflow-hidden xl:max-w-90"
          >
            {searchResults.length ? (
              <Card className="min-w-0 overflow-hidden py-4">
                <CardContent className="flex min-w-0 flex-col gap-2">
                  <h3 className="m-0 text-base font-semibold">Search Results</h3>
                  <Separator />
                  <div className="flex max-h-75 min-w-0 flex-col gap-1 overflow-y-auto">
                    {searchResults.map((node) => {
                      const isSelected = selectedNodeIds.includes(node._id)
                      return (
                        <Button
                          key={node._id}
                          size="sm"
                          variant={isSelected ? 'default' : 'outline'}
                          className={cn(
                            'w-full min-w-0 max-w-full shrink justify-start overflow-hidden',
                            !isSelected &&
                              'border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand'
                          )}
                          onClick={() => {
                            graphCanvasRef.current?.revealNode(node._id)
                            handleToggleNode(node._id)
                          }}
                        >
                          <span className="min-w-0 truncate">
                            {String(node.name ?? node.id ?? node._id)}
                          </span>
                        </Button>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Joy `<Card variant="soft">` → card with the muted sunk-surface
                background to differentiate the detail rail from the canvas card.
                Debug-only, and mounted only for a selected node so it never
                occupies the rail as an empty placeholder. */}
            {debugMode && selectedNode ? (
              <Card className="min-w-0 overflow-hidden bg-bg-sunk py-4">
                <CardContent className="flex min-w-0 flex-col gap-2">
                  <div className="flex min-w-0 flex-row flex-wrap items-center justify-between gap-2">
                    <h3 className="m-0 text-base font-semibold">Node Details</h3>
                    <div className="flex flex-row gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/80 hover:text-brand"
                        disabled={!selectedNodeDetail || detailFetching}
                        onClick={() => {
                          if (!branchGuard.ensureWritable()) return
                          setEditModalOpen(true)
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className={dangerSoftClass}
                        disabled={deleteNodeState.isLoading}
                        onClick={() => void handleDeleteNode()}
                      >
                        {deleteNodeState.isLoading ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : null}
                        Delete
                      </Button>
                    </div>
                  </div>
                  <Separator />
                  <div className="flex min-w-0 flex-col gap-3">
                    <h4 className="m-0 min-w-0 break-all text-sm font-semibold [overflow-wrap:anywhere]">
                      {String(
                        selectedNodeDetail?.name ??
                          selectedNodeDetail?.id ??
                          selectedNode.name ??
                          selectedNode.id
                      )}
                    </h4>
                    <Badge className="max-w-full self-start border border-line bg-transparent text-ink-2">
                      <span className="min-w-0 truncate">
                        {String(selectedNodeDetail?.label ?? selectedNode.label ?? '')}
                      </span>
                    </Badge>
                    <p className="m-0 min-w-0 break-all text-xs text-ink-3 [overflow-wrap:anywhere]">
                      Internal Id: {selectedNode._id}
                    </p>
                    {detailError ? (
                      <div className="flex flex-row flex-wrap items-center gap-2 rounded-md bg-st-down-bg p-2 text-sm text-st-down">
                        <span>Could not load full node.</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-st-down hover:bg-st-down/10 hover:text-st-down"
                          onClick={() => void refetchNodeDetail()}
                        >
                          Retry
                        </Button>
                      </div>
                    ) : null}
                    {detailFetching && !selectedNodeDetail ? (
                      <div className="flex flex-row items-center gap-2">
                        <Loader2 className="size-4 animate-spin text-ink-3" />
                        <span className="text-sm text-ink-3">Loading full properties…</span>
                      </div>
                    ) : null}
                    <Separator />
                    <div className="min-w-0 max-w-full overflow-x-auto">
                      <pre
                        data-testid="node-properties"
                        className="m-0 max-h-[min(60vh,30rem)] w-full min-w-0 max-w-full overflow-auto rounded-md bg-bg-elev/40 p-2 font-mono text-xs leading-relaxed break-all whitespace-pre-wrap text-ink [overflow-wrap:anywhere]"
                      >
                        {JSON.stringify(selectedNodeDetail ?? selectedNode, null, 2)}
                      </pre>
                    </div>
                    <p className="m-0 text-xs text-ink-3">
                      {selectedNodeDetail
                        ? 'Full record from the server.'
                        : 'Summary from the canvas until details load.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>
        ) : null}
      </div>

      {debugMode && selectedNode ? (
        <NodeEditModal
          open={editModalOpen}
          node={selectedNodeDetail ?? selectedNode}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => {
            void refetchGraph()
            void refetchNodeDetail()
          }}
        />
      ) : null}
      {branchGuard.dialog}
    </div>
  )
}

export default GraphPage
